import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Grid
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import { DataService } from 'src/services/data-service';

interface Installment {
  id: string;
  issuedAt: string;
  dueDate: string;
  status: string;
  total: number;
  currency: string | null;
  fee: {
    value: number;
    feeTypeId: string;
    type: string | null;
    currencyId: string;
    currency: string | null;
    billingCycle: string;
    notes: string;
    id: string;
    active: boolean;
  };
}

interface UpdateInstallmentTotalDialogProps {
  open: boolean;
  onClose: () => void;
  installment: Installment | null;
  onSuccess: () => void;
}

const UpdateInstallmentTotalDialog: React.FC<UpdateInstallmentTotalDialogProps> = ({
  open,
  onClose,
  installment,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newTotal, setNewTotal] = useState<number>(0);
  const [reason, setReason] = useState<string>('');

  useEffect(() => {
    if (open && installment) {
      setNewTotal(installment.total);
      setReason('');
      setError(null);
    }
  }, [open, installment]);

  const handleSubmit = async () => {
    if (!installment) return;

    // Validation
    if (!newTotal || newTotal <= 0) {
      setError('Please enter a valid amount greater than 0.');
      return;
    }

    if (!reason || reason.trim() === '') {
      setError('Please provide a reason for updating the total.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        installmentId: installment.id,
        newTotal: newTotal,
        reason: reason.trim()
      };

      const response = await DataService.post('api/Installment/update-total', payload);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update installment total');
      }

      const responseData = await response.json();

      if (!responseData.success) {
        throw new Error(responseData.message || 'Failed to update installment total');
      }

      // Success
      onSuccess();
    } catch (error) {
      console.error('Error updating installment total:', error);
      setError(error instanceof Error ? error.message : 'Failed to update installment total. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setNewTotal(0);
      setReason('');
      onClose();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!installment) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon />
          Update Installment Total
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Installment Information */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight="bold">
            Installment Details
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Issued Date:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(installment.issuedAt)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Due Date:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(installment.dueDate)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Current Total:</strong>
              </Typography>
              <Typography variant="h6" color="primary.main" fontWeight="bold">
                {installment.currency || '$'} {installment.total.toLocaleString()}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Status:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {installment.status}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Update Form */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="New Total Amount"
              type="number"
              value={newTotal}
              onChange={(e) => setNewTotal(parseFloat(e.target.value) || 0)}
              inputProps={{ min: 0, step: 0.01 }}
              required
              InputProps={{
                startAdornment: <AttachMoneyIcon color="primary" sx={{ mr: 1 }} />
              }}
              helperText={`Current: ${installment.currency || '$'} ${installment.total.toLocaleString()}`}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Reason for Update"
              multiline
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why the total is being updated..."
              required
              InputProps={{
                startAdornment: <DescriptionIcon color="primary" sx={{ mr: 1, alignSelf: 'flex-start', mt: 1 }} />
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          size="small"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          size="small"
          disabled={loading || !newTotal || newTotal <= 0 || !reason.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
        >
          {loading ? 'Updating...' : 'Update Total'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateInstallmentTotalDialog;
